import { Box } from "@mui/material";

import { iconGutterWidth } from "./cellOverlayStyles";

export interface CellReviewShellProps {
  /** Resolved background colour for the cell (transparent, yellow, blue, etc.). */
  backgroundColor: string;
  /** Slot for renderer-specific overlays (status icons, corner indicators). */
  overlays?: React.ReactNode;
  /**
   * How many status icons `overlays` positions along the cell's left edge. The
   * shell reserves room for them on both sides so they never sit on the text.
   */
  iconCount?: number;
  className?: string;
  children: React.ReactNode;
}

/**
 * Presentational shell shared by cell renderers that show cell-review
 * decorations (background tint, plus the corners renderers pass as overlays).
 *
 * The shell is deliberately *not* `position: relative` and has no height of its
 * own. AG Grid positions each `.ag-cell` absolutely, so it is the nearest
 * positioned ancestor and the decorations below anchor to the full cell — the
 * corners, whatever the row height. Giving the shell a height instead would put
 * a ceiling on the content AG Grid's `autoHeight` measures, and wrapped text
 * would overflow its row rather than growing it.
 */
export const CellReviewShell = ({
  backgroundColor,
  overlays,
  iconCount = 0,
  className,
  children,
}: CellReviewShellProps): React.JSX.Element => {
  const gutter = iconGutterWidth(iconCount);

  return (
    <Box
      className={className}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: gutter === 0 ? 1 : `${gutter}px`,
      }}
    >
      <Box
        data-testid="cell-review-tint"
        sx={{ position: "absolute", inset: 0, backgroundColor, zIndex: 0 }}
      />
      {overlays}
      <Box sx={{ position: "relative", zIndex: 1 }}>{children}</Box>
    </Box>
  );
};
